import React from "react";
import PropTypes from "prop-types";
import ReactHtmlParser from "react-html-parser";
import { FormattedMessage } from "react-intl";
import { Col, Row } from "react-bootstrap";
import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import { replaceLinks } from "helpers/urlHelpers";
import { replaceLinebreaks } from "helpers/commonHelpers";
import CardNew from "components/molecules/Card/CardNew";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import { useGetCostRecoverySetting } from "api/queries/location/useGetCostRecoverySetting";
import usePermissions from "hooks/usePermissions";

const QuickOrganisationalDetails = function ({ data, loadingData, isDrawer }) {
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isProvider = user_type === "provider";
  const { data: costRecoverySetting } = useGetCostRecoverySetting(
    data?.organization?.payment_location_reference,
    { ...(data?.organization?.reason_code && { reason_code: data.organization.reason_code }) },
    {
      enabled: !!data?.organization?.payment_location_reference && !isProvider,
    },
  );

  const {
    quick_request_reference,
    quick_request_title,
    payment_location,
    invoice_location,
    quick_request_created_by_name,
    quick_request_created_by_email,
    cost_center,
    reason_code,
    custom_field1,
    custom_field2,
    custom_field3,
    custom_longfield1,
    quick_request_creation_date,
    quick_request_creation_time,
    payment_location_reference,
    reason_code_group,
    cost_recovery_required,
    custom_cost_recovery_field1,
    custom_cost_recovery_field2,
  } = data?.organization || {};

  return (
    <CardNew
      className={`${isDrawer ? "" : "mb-15"} font-b3`}
      isLoading={loadingData}
      title={<FormattedMessage id="quote-request-detail.organisationalDetails" defaultMessage="Organisational details" />}
    >
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quick-request-detail.org.quickRequestNumber" defaultMessage="Quick request number" />
        </Col>
        <Col>{quick_request_reference}</Col>
      </Row>
      {quick_request_title && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="general.shipmentTitle"
              defaultMessage="Shipment Title"
              values={{ shipmentTitle: getLabel("Shipment title", "capitalize-first", payment_location_reference) }}
            />
          </Col>
          <Col>{quick_request_title || "-"}</Col>
        </Row>
      )}
      {payment_location && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="general.paymentLocation"
              values={{ paymentLocation: getLabel("Payment location", "capitalize-first", payment_location_reference) }}
              defaultMessage="Payment location"
            />
          </Col>
          <Col>{payment_location}</Col>
        </Row>
      )}
      {invoice_location && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.org.quickRequestInvoiceLocation" defaultMessage="Invoice location" />
          </Col>
          <Col>{invoice_location}</Col>
        </Row>
      )}
      {quick_request_created_by_name && quick_request_created_by_email && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quick-request-detail.org.quickRequestCreatedBy" defaultMessage="Created by" />
          </Col>
          <Col>{`${quick_request_created_by_name} (${quick_request_created_by_email})`}</Col>
        </Row>
      )}

      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="general.createdDate" defaultMessage="Creation Date" />
        </Col>
        <Col>
          {formatDate(`${quick_request_creation_date} ${quick_request_creation_time}`, DateTimeFormat.fullDateTimeFormat)}
        </Col>
      </Row>

      {cost_center && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Cost Center / WBS / Project #", {
              paymentLocation: payment_location_reference,
              style: "capitalize",
              translation: <FormattedMessage id="quote-request-detail.costCenter" defaultMessage="Cost center" />,
            })}
          </Col>
          <Col>{cost_center || "-"}</Col>
        </Row>
      )}
      {reason_code && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Reason code", {
              style: "capitalize",
              translation: <FormattedMessage id="quote-request-detail.reasonCode" defaultMessage="Reason code" />,
            })}
          </Col>
          <Col>{reason_code}</Col>
        </Row>
      )}
      {reason_code_group && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Reason code group", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: <FormattedMessage id="quote-request-detail.reasonCodeGroup" defaultMessage="Reason code group" />,
            })}
          </Col>
          <Col>{reason_code_group || "-"}</Col>
        </Row>
      )}
      {custom_field1 && (
        <Row className="mb-2">
          <Col>
            <span className="text-secondary">
              {getLabelWithTranslationFallback("Custom field 01", {
                style: "capitalize",
                paymentLocation: payment_location_reference,
                translation: (
                  <FormattedMessage
                    id="new-request.quote-confirmation.organisation.customField1"
                    defaultMessage="Custom field 1"
                  />
                ),
              })}
            </span>
          </Col>
          <Col>{custom_field1 || "-"}</Col>
        </Row>
      )}
      {custom_field2 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom field 02", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField2"
                  defaultMessage="Custom field 2"
                />
              ),
            })}
          </Col>
          <Col>{custom_field2 || "-"}</Col>
        </Row>
      )}
      {custom_field3 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom field 03", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField3"
                  defaultMessage="Custom field 3"
                />
              ),
            })}
          </Col>
          <Col>{custom_field3 || "-"}</Col>
        </Row>
      )}
      {custom_longfield1 && (
        <Row className="mb-2">
          <Col>
            <span className="text-secondary">
              {getLabelWithTranslationFallback("Custom longfield 01", {
                style: "capitalize",
                paymentLocation: payment_location_reference,
                translation: (
                  <FormattedMessage
                    id="new-request.quote-confirmation.organisation.customLongfield1"
                    defaultMessage="Custom longfield 1"
                  />
                ),
              })}
            </span>
          </Col>
          <Col>
            <span className="pre-wrap">{ReactHtmlParser(replaceLinebreaks(replaceLinks(custom_longfield1 || "-")))}</span>
          </Col>
        </Row>
      )}
      {costRecoverySetting?.is_cost_recovery_required && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="tasks.costRecovery" defaultMessage="Cost recovery" />
          </Col>
          <Col>{cost_recovery_required ? "Yes" : "No"}</Col>
        </Row>
      )}
      {custom_cost_recovery_field1 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.organisation.customCostRecoveryField1"
              values={{
                customCostRecoveryField1: getLabel(
                  "Custom cost recovery field 01",
                  "capitalize",
                  payment_location_reference,
                ),
              }}
              defaultMessage="Custom cost recovery field 1"
            />
          </Col>
          <Col>{custom_cost_recovery_field1 || "-"}</Col>
        </Row>
      )}
      {custom_cost_recovery_field2 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.organisation.customCostRecoveryField2"
              values={{
                customCostRecoveryField2: getLabel(
                  "Custom cost recovery field 02",
                  "capitalize",
                  payment_location_reference,
                ),
              }}
              defaultMessage="Custom cost recovery field 2"
            />
          </Col>
          <Col>{custom_cost_recovery_field2 || "-"}</Col>
        </Row>
      )}
    </CardNew>
  );
};

QuickOrganisationalDetails.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
  isDrawer: PropTypes.bool,
};

QuickOrganisationalDetails.defaultProps = {
  isDrawer: false,
};

export default QuickOrganisationalDetails;
